/* Landing-page strip that surfaces the 3 most-recent published Insights.
   Renders nothing while loading, and nothing if the feed is empty — the goal is to keep the
   funnel intact when there's no content yet, not to show an awkward empty box. */
import { useEffect, useState } from "react";
import { ArrowRight, ExternalLink } from "lucide-react";
import { api, resolveImg } from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
}

export default function LatestInsights() {
  const [items, setItems] = useState(null); // null = loading, [] = loaded-empty

  useEffect(() => {
    api.get("/insights/articles", { params: { sort: "newest", page: 1, page_size: 3 } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, []);

  const openCard = async (a) => {
    try { await api.post(`/insights/articles/${a.id}/view`); } catch {}
    if (a.linkedin_url) window.open(a.linkedin_url, "_blank", "noopener,noreferrer");
  };

  if (!items || items.length === 0) return null;

  return (
    <section id="insights-strip" data-testid="landing-insights-strip"
      className="bg-upland-offwhite border-t border-upland-brown/15">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] text-upland-brown mb-3">Latest from LinkedIn</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-upland-green">
              Field notes from the practice.
            </h2>
          </div>
          <a href="/content" data-testid="landing-insights-all"
            className="group inline-flex items-center gap-2 text-sm tracking-wide text-upland-green hover:text-upland-brown transition-colors self-start md:self-auto">
            Read all insights <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((a) => {
            const permalink = a.slug ? `/content/${a.slug}` : "#";
            const handleClick = (e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
              e.preventDefault();
              openCard(a);
            };
            return (
              <a key={a.id} href={permalink} onClick={handleClick}
                data-testid={`landing-insight-${a.id}`}
                className="text-left flex flex-col bg-white border border-upland-brown/20 hover:border-upland-green/60 transition-colors group focus:outline-none focus:ring-2 focus:ring-upland-green/40">
                <div className="aspect-[16/10] bg-upland-creme overflow-hidden">
                  {a.image_url ? (
                    <img src={resolveImg(a.image_url)} alt={a.title} loading="lazy" decoding="async" width="800" height="500"
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-upland-brown/40 font-serif text-2xl">Upland Group</div>
                  )}
                </div>
                <div className="p-5 flex-1 flex flex-col gap-3">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-upland-brown">
                    {a.category_name && <span>{a.category_name}</span>}
                    {a.category_name && a.published_at && <span aria-hidden>·</span>}
                    {a.published_at && <span>{fmtDate(a.published_at)}</span>}
                  </div>
                  <h3 className="font-serif text-xl text-upland-green leading-tight">{a.title}</h3>
                  {a.excerpt && <p className="text-sm text-upland-grey leading-relaxed line-clamp-3">{a.excerpt}</p>}
                  <span className="mt-auto pt-3 inline-flex items-center gap-1.5 text-sm text-upland-green group-hover:text-upland-brown transition-colors">
                    Read on LinkedIn <ExternalLink size={14} />
                  </span>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
